import { FormEvent, KeyboardEvent, useMemo, useRef, useState } from "react";
import { useBinsByWarehouse } from "../hooks/queries/useBins";
import {
  InventoryMovement,
  useCreateManualMovement,
  useInventoryMovements,
} from "../hooks/queries/useInventory";
import { useWarehouses } from "../hooks/queries/useWarehouses";
import {
  ApiError,
  getApiErrorMessage,
  resolveItemReference,
  ResolvedItem,
} from "../lib/api";
import { useAuthStore } from "../stores/authStore";
import { ScanOverlay } from "../components/ScanOverlay";

export function CreateMovementPage() {
  const { user, userUser } = useAuthStore();
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const createSectionRef = useRef<HTMLElement | null>(null);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [movementWarehouseId, setMovementWarehouseId] = useState("");
  const [movementDateFrom, setMovementDateFrom] = useState("");
  const [movementDateTo, setMovementDateTo] = useState("");
  const [barcodeOrItemId, setBarcodeOrItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [selectedBinId, setSelectedBinId] = useState("");
  const [resolvedItem, setResolvedItem] = useState<ResolvedItem | null>(null);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isResolvingItem, setIsResolvingItem] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const warehousesQuery = useWarehouses();
  const createMovementMutation = useCreateManualMovement();
  const movementFilters = useMemo(
    () => ({
      ...(movementWarehouseId ? { warehouse_id: movementWarehouseId } : {}),
      ...(movementDateFrom
        ? { date_from: new Date(`${movementDateFrom}T00:00:00`).toISOString() }
        : {}),
      ...(movementDateTo
        ? { date_to: new Date(`${movementDateTo}T23:59:59.999`).toISOString() }
        : {}),
      limit: 50,
    }),
    [movementDateFrom, movementDateTo, movementWarehouseId],
  );
  const movementsQuery = useInventoryMovements(movementFilters);
  const canCreateMovement = user?.role === "owner" && Boolean(userUser);

  const selectedWarehouse = useMemo(
    () =>
      (warehousesQuery.data || []).find(
        (warehouse) => warehouse.id === selectedWarehouseId,
      ),
    [selectedWarehouseId, warehousesQuery.data],
  );

  const binsQuery = useBinsByWarehouse(
    selectedWarehouse?.use_bins ? selectedWarehouseId : undefined,
  );

  if (user?.role !== "owner") {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-900 shadow-sm">
            <h1 className="text-2xl font-bold">Access Denied</h1>
            <p className="mt-3 text-sm leading-6">
              This page is only accessible to the owner account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const resetScanFields = () => {
    setBarcodeOrItemId("");
    setQuantity("");
    setNote("");
    setOccurredAt("");
    setResolvedItem(null);
    setBarcodeError(null);
    setFormError(null);
    barcodeInputRef.current?.focus();
  };

  const resolveItem = async () => {
    const value = barcodeOrItemId.trim();

    setBarcodeError(null);
    setFormError(null);
    setSuccessMessage(null);

    if (!value) {
      setResolvedItem(null);
      setBarcodeError("Scan a barcode, or enter a SKU");
      return;
    }

    setIsResolvingItem(true);

    try {
      setResolvedItem(await resolveItemReference(value));
    } catch (error) {
      setResolvedItem(null);

      if (error instanceof ApiError && error.status === 404) {
        setBarcodeError("Item not found");
        return;
      }
      setBarcodeError(getApiErrorMessage(error, "Failed to resolve item"));
    } finally {
      setIsResolvingItem(false);
    }
  };

  const handleWarehouseChange = (warehouseId: string) => {
    setSelectedWarehouseId(warehouseId);
    setSelectedBinId("");
    setFormError(null);
    setSuccessMessage(null);
  };

  const handleScanResult = (value: string) => {
    setBarcodeOrItemId(value);
    setResolvedItem(null);
    setBarcodeError(null);
    setSuccessMessage(null);
    setShowScanner(false);
    setTimeout(async () => {
      const trimmed = value.trim();
      if (!trimmed) return;
      setIsResolvingItem(true);
      try {
        setResolvedItem(await resolveItemReference(trimmed));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          setBarcodeError("Item not found");
        } else {
          setBarcodeError(getApiErrorMessage(error, "Failed to resolve item"));
        }
      } finally {
        setIsResolvingItem(false);
      }
    }, 0);
  };

  const handleBarcodeKeyDown = async (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    await resolveItem();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsedQuantity = Number(quantity);
    setFormError(null);
    setSuccessMessage(null);

    if (!selectedWarehouseId) {
      setFormError("Select a warehouse");
      return;
    }

    if (!resolvedItem) {
      setBarcodeError("Resolve an item before submitting");
      return;
    }

    if (!Number.isInteger(parsedQuantity) || parsedQuantity === 0) {
      setFormError("Quantity must be a non-zero integer");
      return;
    }

    if (selectedWarehouse?.use_bins && !selectedBinId) {
      setFormError("Select a bin for this warehouse");
      return;
    }

    let occurredAtIso: string | undefined;
    if (occurredAt) {
      const parsedDate = new Date(`${occurredAt}T00:00:00`);
      if (parsedDate.getTime() > Date.now()) {
        setFormError("Date cannot be in the future");
        return;
      }
      occurredAtIso = parsedDate.toISOString();
    }

    try {
      const result = await createMovementMutation.mutateAsync({
        item_id: resolvedItem.id,
        warehouse_id: selectedWarehouseId,
        quantity: parsedQuantity,
        ...(selectedBinId ? { bin_id: selectedBinId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(occurredAtIso ? { occurred_at: occurredAtIso } : {}),
      });

      const selectedBin = (binsQuery.data || []).find(
        (bin) => bin.id === selectedBinId,
      );

      const direction = result.quantity > 0 ? "+" : "";
      setSuccessMessage(
        `Manual adjustment created: ${direction}${result.quantity} qty of ${resolvedItem.name} in ${selectedWarehouse?.name}${selectedBin ? ` (${selectedBin.name})` : ""}`,
      );
      resetScanFields();
    } catch (error) {
      setFormError(getApiErrorMessage(error, "Failed to create movement"));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Movements
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Review recent inventory movements and create manual adjustments when
            needed.
          </p>
        </div>

        <section className="rounded-lg border border-gray-200 bg-white shadow-sm p-6 space-y-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Recent Movements
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                The latest 50 movement records across add, remove, transfer,
                count, and manual adjustment flows.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                createSectionRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
              }
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
            >
              Create Movement
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label
                htmlFor="movement-warehouse"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Warehouse
              </label>
              <select
                id="movement-warehouse"
                value={movementWarehouseId}
                onChange={(e) => setMovementWarehouseId(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              >
                <option value="">All warehouses</option>
                {(warehousesQuery.data || []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="movement-date-from"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                From
              </label>
              <input
                id="movement-date-from"
                type="date"
                value={movementDateFrom}
                onChange={(e) => setMovementDateFrom(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              />
            </div>

            <div>
              <label
                htmlFor="movement-date-to"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                To
              </label>
              <input
                id="movement-date-to"
                type="date"
                value={movementDateTo}
                onChange={(e) => setMovementDateTo(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              />
            </div>
          </div>

          {movementsQuery.isLoading ? (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-12 animate-pulse rounded bg-gray-200"
                />
              ))}
            </div>
          ) : movementsQuery.error ? (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
              Failed to load movement history.
            </div>
          ) : !movementsQuery.data || movementsQuery.data.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-sm text-gray-500">
              No movements found for the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      When
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Item
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Route
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      User
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Note
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {movementsQuery.data.map((movement) => {
                    const displayedTimestamp =
                      movement.occurred_at ?? movement.created_at;

                    return (
                      <tr key={movement.id} className="align-top">
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                          <div className="font-medium text-gray-900">
                            {formatMovementTimestamp(displayedTimestamp)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {movement.occurred_at ? "Occurred" : "Recorded"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${MOVEMENT_TYPE_STYLES[movement.type]}`}
                          >
                            {MOVEMENT_TYPE_LABELS[movement.type]}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {movement.item_name}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {formatMovementQuantity(movement)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {formatMovementRoute(movement)}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {movement.user_name ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {movement.note ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section
          ref={createSectionRef}
          className="bg-white rounded-lg shadow p-6 scroll-mt-6"
        >
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Create Manual Movement
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Create a MANUAL_ADJUSTMENT movement directly, bypassing stock
                guards. Positive quantity adds stock; negative quantity removes
                stock.
              </p>
            </div>
          </div>

          {!canCreateMovement && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              Manual movements must be created while signed in as a personal
              user. Use the inventory workspace to continue as a user while
              keeping your owner session active.
              <div className="mt-3">
                <a
                  href="/dashboard/inventory"
                  className="inline-flex items-center rounded bg-amber-800 px-4 py-2 font-medium text-white hover:bg-amber-900"
                >
                  Continue as User
                </a>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {successMessage && (
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-800">
                {successMessage}
              </div>
            )}

            {(formError || barcodeError) && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
                {formError || barcodeError}
              </div>
            )}

            <div>
              <label
                htmlFor="warehouse"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Warehouse
              </label>
              <select
                id="warehouse"
                value={selectedWarehouseId}
                onChange={(e) => handleWarehouseChange(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={createMovementMutation.isPending || !canCreateMovement}
              >
                <option value="">Select a warehouse</option>
                {(warehousesQuery.data || []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedWarehouse?.use_bins && (
              <div>
                <label
                  htmlFor="bin"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Bin
                </label>
                <select
                  id="bin"
                  value={selectedBinId}
                  onChange={(e) => setSelectedBinId(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={
                    createMovementMutation.isPending || !canCreateMovement
                  }
                >
                  <option value="">Select a bin</option>
                  {(binsQuery.data || []).map((bin) => (
                    <option key={bin.id} value={bin.id}>
                      {bin.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label
                htmlFor="item"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Item
              </label>
              <div className="flex gap-2">
                <input
                  ref={barcodeInputRef}
                  id="item"
                  type="text"
                  value={barcodeOrItemId}
                  onChange={(e) => {
                    setBarcodeOrItemId(e.target.value);
                    setResolvedItem(null);
                    setBarcodeError(null);
                  }}
                  onBlur={() => {
                    if (barcodeOrItemId.trim()) {
                      void resolveItem();
                    }
                  }}
                  onKeyDown={handleBarcodeKeyDown}
                  autoFocus
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Scan a barcode, or enter a SKU"
                  disabled={createMovementMutation.isPending || !canCreateMovement}
                />
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={createMovementMutation.isPending || !canCreateMovement}
                >
                  📷 Scan
                </button>
                <button
                  type="button"
                  onClick={() => void resolveItem()}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    createMovementMutation.isPending ||
                    isResolvingItem ||
                    !canCreateMovement
                  }
                >
                  {isResolvingItem ? "Resolving..." : "Resolve"}
                </button>
              </div>

              <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                {resolvedItem
                  ? `Resolved item: ${resolvedItem.name}${resolvedItem.sku ? ` (SKU: ${resolvedItem.sku})` : ""}`
                  : "Scan a barcode, or enter a SKU"}
              </div>
            </div>

            <div>
              <label
                htmlFor="quantity"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Quantity{" "}
                <span className="text-gray-500 font-normal">
                  (positive = stock in, negative = stock out)
                </span>
              </label>
              <input
                id="quantity"
                type="number"
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="e.g. 10 or -5"
                disabled={createMovementMutation.isPending || !canCreateMovement}
              />
            </div>

            <div>
              <label
                htmlFor="occurred-at"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Date{" "}
                <span className="text-gray-500 font-normal">
                  (optional, defaults to now)
                </span>
              </label>
              <input
                id="occurred-at"
                type="date"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={createMovementMutation.isPending || !canCreateMovement}
              />
              <p className="mt-1 text-xs text-gray-500">
                Set an earlier date to record this movement as having
                happened in the past.
              </p>
            </div>

            <div>
              <label
                htmlFor="note"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Note{" "}
                <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={500}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Reason for adjustment, e.g. write-off, opening balance correction"
                disabled={createMovementMutation.isPending || !canCreateMovement}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={createMovementMutation.isPending || !canCreateMovement}
              >
                {createMovementMutation.isPending
                  ? "Creating..."
                  : "Create Movement"}
              </button>
            </div>
          </form>
        </section>

        {showScanner && (
          <ScanOverlay
            onBarcodeScan={handleScanResult}
            onClose={() => setShowScanner(false)}
          />
        )}
      </div>
    </div>
  );
}

const MOVEMENT_TYPE_LABELS: Record<InventoryMovement["type"], string> = {
  ADD: "Add",
  REMOVE: "Remove",
  TRANSFER: "Transfer",
  COUNT_ADJUSTMENT: "Count Adjust",
  MANUAL_ADJUSTMENT: "Manual Adjust",
};

const MOVEMENT_TYPE_STYLES: Record<InventoryMovement["type"], string> = {
  ADD: "bg-emerald-100 text-emerald-800",
  REMOVE: "bg-rose-100 text-rose-800",
  TRANSFER: "bg-sky-100 text-sky-800",
  COUNT_ADJUSTMENT: "bg-amber-100 text-amber-900",
  MANUAL_ADJUSTMENT: "bg-slate-200 text-slate-900",
};

function formatMovementTimestamp(value: string) {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatMovementQuantity(movement: InventoryMovement) {
  const quantity =
    movement.type === "REMOVE" || !movement.dest_warehouse_id
      ? -movement.quantity
      : movement.quantity;

  return quantity > 0 ? `+${quantity}` : String(quantity);
}

function formatMovementRoute(movement: InventoryMovement) {
  const source = [movement.source_warehouse_name, movement.source_bin_name]
    .filter(Boolean)
    .join(" / ");
  const destination = [movement.dest_warehouse_name, movement.dest_bin_name]
    .filter(Boolean)
    .join(" / ");

  if (source && destination) {
    return `${source} -> ${destination}`;
  }

  if (source) {
    return `From ${source}`;
  }

  if (destination) {
    return `To ${destination}`;
  }

  return "—";
}
